import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Settings, Save, Mail, CreditCard, Loader2, Shield, Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function AccountSettingsPage() {
  const { user, logout, fetchUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ display_name: '', first_name: '', last_name: '', phone: '' });
  const [saving, setSaving] = useState(false);

  // Password state
  const [hasPassword, setHasPassword] = useState(false);
  const [pwMode, setPwMode] = useState(null); // null | 'set' | 'change'
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    api.get('/account/settings').then(r => {
      setData(r.data);
      setForm({
        display_name: r.data.user?.display_name || '',
        first_name: r.data.profile?.first_name || '',
        last_name: r.data.profile?.last_name || '',
        phone: r.data.profile?.phone || '',
      });
    }).catch(() => {}).finally(() => setLoading(false));

    // Check password status
    api.get('/auth/me').then(r => {
      setHasPassword(r.data.has_password || false);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try { await api.put('/account/settings', form); toast.success('Settings saved'); } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const handlePasswordSubmit = async () => {
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (pwForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setPwSaving(true);
    try {
      if (hasPassword) {
        await api.post('/account/password/change', {
          current_password: pwForm.current_password,
          new_password: pwForm.new_password,
        });
      } else {
        await api.post('/account/password/set', {
          password: pwForm.new_password,
        });
      }
      toast.success(hasPassword ? 'Password changed' : 'Password set successfully');
      setHasPassword(true);
      setPwMode(null);
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div data-testid="account-settings-page">
      <h1 className="font-display text-2xl font-medium text-ink mb-1">Account Settings</h1>
      <p className="text-sm text-muted-foreground mb-6">Manage your profile and account preferences.</p>

      {/* Profile */}
      <Card className="bg-white border-stone/50 mb-4">
        <CardContent className="p-5">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-4">Profile</h3>
          <div className="space-y-3">
            <div><Label className="text-xs">Display Name</Label><Input value={form.display_name} onChange={e => setForm({...form, display_name: e.target.value})} className="mt-1" placeholder="Your name" data-testid="settings-display-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">First Name</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} className="mt-1" /></div>
              <div><Label className="text-xs">Last Name</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1" placeholder="(555) 123-4567" /></div>
            <Button size="sm" className="gap-1" onClick={handleSave} disabled={saving}><Save className="h-3 w-3" /> {saving ? 'Saving...' : 'Save Changes'}</Button>
          </div>
        </CardContent>
      </Card>

      {/* Email & Sign-In */}
      <Card className="bg-white border-stone/50 mb-4">
        <CardContent className="p-5">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Mail className="h-3 w-3" /> Email & Sign-In</h3>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm text-ink">{data?.user?.email}</p>
              <p className="text-xs text-muted-foreground">Sign in with magic link or password</p>
            </div>
            <div className="flex gap-1.5">
              <Badge variant="outline" className="text-[10px]"><Mail className="h-2.5 w-2.5 mr-0.5" /> Magic Link</Badge>
              {hasPassword && <Badge variant="outline" className="text-[10px]"><Lock className="h-2.5 w-2.5 mr-0.5" /> Password</Badge>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Management */}
      <Card className="bg-white border-stone/50 mb-4" data-testid="password-section">
        <CardContent className="p-5">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Lock className="h-3 w-3" /> Password</h3>

          {!pwMode ? (
            <div>
              {hasPassword ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-ink flex items-center gap-1.5"><CheckCircle className="h-3.5 w-3.5" style={{ color: '#0E6B5D' }} /> Password is set</p>
                    <p className="text-xs text-muted-foreground mt-0.5">You can sign in with email + password or magic link.</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setPwMode('change')} data-testid="change-password-btn">Change Password</Button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-ink">No password set</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Add a password for an additional sign-in option.</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setPwMode('set')} data-testid="set-password-btn">Set Password</Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {pwMode === 'change' && (
                <div>
                  <Label className="text-xs">Current Password</Label>
                  <Input
                    type="password"
                    value={pwForm.current_password}
                    onChange={e => setPwForm({...pwForm, current_password: e.target.value})}
                    className="mt-1"
                    placeholder="Enter current password"
                    data-testid="current-password-input"
                  />
                </div>
              )}
              <div>
                <Label className="text-xs">New Password</Label>
                <Input
                  type="password"
                  value={pwForm.new_password}
                  onChange={e => setPwForm({...pwForm, new_password: e.target.value})}
                  className="mt-1"
                  placeholder="At least 8 characters"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <Label className="text-xs">Confirm Password</Label>
                <Input
                  type="password"
                  value={pwForm.confirm_password}
                  onChange={e => setPwForm({...pwForm, confirm_password: e.target.value})}
                  className="mt-1"
                  placeholder="Repeat password"
                  data-testid="confirm-password-input"
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="gap-1" onClick={handlePasswordSubmit} disabled={pwSaving} data-testid="save-password-btn">
                  <Lock className="h-3 w-3" /> {pwSaving ? 'Saving...' : (pwMode === 'set' ? 'Set Password' : 'Change Password')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setPwMode(null); setPwForm({ current_password: '', new_password: '', confirm_password: '' }); }}>Cancel</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Methods — preview placeholder */}
      <Card className="bg-white border-stone/50 mb-4">
        <CardContent className="p-5">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><CreditCard className="h-3 w-3" /> Payment Methods</h3>
          <div className="p-4 rounded-lg" style={{ background: 'hsl(var(--bg-paper-2))' }}>
            <p className="text-xs text-muted-foreground text-center">Saved payment methods will be available after Stripe activation.</p>
            <p className="text-[10px] text-muted-foreground text-center mt-1">Currently using preview payment mode.</p>
          </div>
        </CardContent>
      </Card>

      {/* Sign out */}
      <Card className="bg-white border-stone/50">
        <CardContent className="p-5 flex items-center justify-between">
          <div><p className="text-sm text-ink">Sign Out</p><p className="text-xs text-muted-foreground">Sign out of your account on this device</p></div>
          <Button variant="outline" size="sm" onClick={logout}>Sign Out</Button>
        </CardContent>
      </Card>
    </div>
  );
}
